/**
 * HORO-10599: Zara's registered-free 3-message trial — modeled directly on
 * useTarotCredits (common/chat/tarot/tarotCredits.js)'s tri-state shape.
 * Unlike tarot this never resets: it's a one-time conversion funnel (3 free
 * messages, then subscribe), tracked on the user document
 * (`zaraTrial.messagesUsed`, backend service/advisor/zaraTrial.ts) and
 * synced into Redux via `zaraTrial` on the /user fetch and the chat-send
 * response (setUserZaraTrial).
 */
import { useSelector } from 'react-redux';

import { ZARA_TRIAL_MESSAGE_LIMIT, isPremiumSubscriber } from '../service/advisorAccess';
import { getUser, getUserZaraTrial } from '../store/slices/authSlice';

export const useZaraTrial = () => {
    const user = useSelector(getUser);
    const zaraTrial = useSelector(getUserZaraTrial);
    const isUser = !!user?.id;
    const isPaidSubscriber = isPremiumSubscriber(user);

    // Premium subscribers: no trial cap at all — unlimited, same as tarot's
    // paid-subscriber branch.
    if (isPaidSubscriber) {
        return { used: 0, limit: ZARA_TRIAL_MESSAGE_LIMIT, isExhausted: false };
    }

    // Registered free/trial users: the user document's `zaraTrial` (synced
    // on /user fetch and after each send) is the source of truth. Fall back
    // to 0 used if it hasn't propagated yet (e.g. very first render before
    // /user resolves) — optimistic-unlocked rather than optimistic-locked.
    if (isUser) {
        const used = zaraTrial?.used ?? 0;
        return { used, limit: ZARA_TRIAL_MESSAGE_LIMIT, isExhausted: used >= ZARA_TRIAL_MESSAGE_LIMIT };
    }

    // Anonymous: zero entitlement, unconditional — Zara is the register
    // upsell, not a free-to-chat advisor. Registering is what unlocks the
    // 3-message trial in the first place.
    return { used: 0, limit: ZARA_TRIAL_MESSAGE_LIMIT, isExhausted: true };
};

export default useZaraTrial;
